/**
 * 应用入口
 * ------------------------------------------------------------
 * 职责只有四件事：
 *   1. 选存储适配器、初始化全局状态
 *   2. 渲染侧边栏（导航 + 教材切换 + 学生卡片）
 *   3. 注册路由
 *   4. 启动路由
 *
 * 页面（pages/*）只负责渲染自己那块，不碰启动逻辑。
 */
import './styles/base.css';
import './styles/layout.css';
import './styles/components.css';
import './styles/app.css';
import './styles/study.css';

import { createStorage } from './core/storage/index.js';
import { bootstrap, state, switchStudent, listStudents, createStudent, levelFromXp, saveSelection } from './core/store.js';
import { on, EVENTS } from './core/events.js';
import { startRouter, route, setNotFound, navigate, currentPath } from './core/router.js';
import { listTextbooks, listUnits, loadCatalog } from './services/dataSource.js';
import { closeModal, openModal, emptyState } from './components/ui.js';
import { toast, escapeHtml } from './utils/index.js';

/* 页面（使用动态 import 让首次加载更快，也便于新增页面） */
import renderHome from './pages/home.js';
import renderTextbooks from './pages/textbooks.js';
import renderPractice from './pages/practice.js';
import renderSession from './pages/session.js';
import renderFlow from './pages/flow.js';
import renderGames from './pages/games.js';
import renderReport from './pages/report.js';
import renderReview from './pages/review.js';
import renderImport from './pages/import.js';
import renderOcrReview from './pages/ocr-review.js';
import renderSettings from './pages/settings.js';

const NAV = [
  { key: 'home', path: '#/home', icon: '🏠', label: '首页' },
  { key: 'textbooks', path: '#/textbooks', icon: '📚', label: '教材管理' },
  { key: 'practice', path: '#/practice', icon: '✏️', label: '练习中心' },
  { key: 'games', path: '#/games', icon: '🎮', label: '游戏中心' },
  { key: 'report', path: '#/report', icon: '📊', label: '学习数据' },
  { key: 'review', path: '#/review', icon: '🔁', label: '错题复习' },
  { key: 'import', path: '#/import', icon: '📥', label: '导入中心' },
  { key: 'settings', path: '#/settings', icon: '⚙️', label: '设置' },
];

const APP_VERSION = '1.1.0';

/* ------------------------------ 启动 ------------------------------ */

async function main() {
  createStorage();
  await bootstrap();

  renderNav();
  await refreshContextPicker();
  renderStudentCard();

  bindShellEvents();
  registerRoutes();

  startRouter(document.getElementById('view'));

  // 学习数据变化后，侧边栏学生卡片跟着更新
  on(EVENTS.STUDENT_CHANGED, renderStudentCard);
  on(EVENTS.PROGRESS_CHANGED, renderStudentCard);

  console.info(`[app] 英语学习乐园 v${APP_VERSION} 已启动`);
}

function renderNav() {
  const nav = document.getElementById('nav');
  nav.innerHTML = NAV.map(n =>
    `<a class="nav-item" href="${n.path}" data-nav="${n.key}">
       <span aria-hidden="true">${n.icon}</span><span>${n.label}</span>
     </a>`).join('');
}

/* --------------------------- 教材 / 单元切换 --------------------------- */

async function refreshContextPicker() {
  const tbSel = document.getElementById('sel-textbook');
  const uSel = document.getElementById('sel-unit');
  if (!tbSel || !uSel) return;

  let books = [];
  try { books = await listTextbooks(); } catch (err) { console.warn(err); }

  if (!books.length) {
    tbSel.innerHTML = '<option value="">（暂无教材）</option>';
    uSel.innerHTML = '<option value="">—</option>';
    return;
  }

  // 没选过就默认第一本
  if (!state.selection.textbookId || !books.some(b => b.id === state.selection.textbookId)) {
    state.selection.textbookId = books[0].id;
  }
  tbSel.innerHTML = books.map(b =>
    `<option value="${b.id}" ${b.id === state.selection.textbookId ? 'selected' : ''}>${escapeHtml(b.name)}</option>`
  ).join('');

  await refreshUnitPicker();
}

async function refreshUnitPicker() {
  const uSel = document.getElementById('sel-unit');
  if (!uSel) return;
  const tbId = state.selection.textbookId;
  let units = [];
  try { units = tbId ? await listUnits(tbId) : []; } catch (err) { console.warn(err); }

  if (!units.length) {
    uSel.innerHTML = '<option value="">（暂无单元）</option>';
    return;
  }
  if (!state.selection.unitId || !units.some(u => u.id === state.selection.unitId)) {
    state.selection.unitId = units[0].id;
  }
  uSel.innerHTML = units.map(u =>
    `<option value="${u.id}" ${u.id === state.selection.unitId ? 'selected' : ''}>Unit ${u.no} ${escapeHtml(u.title || '')}</option>`
  ).join('');
  await saveSelection({});
}

/* ------------------------------ 学生卡片 ------------------------------ */

function renderStudentCard() {
  const s = state.student;
  if (!s) return;
  const lv = levelFromXp(s.xp || 0);
  const av = document.getElementById('student-avatar');
  const nm = document.getElementById('student-name');
  const lvm = document.getElementById('student-level');
  if (av) av.textContent = s.avatar || '🦊';
  if (nm) nm.textContent = s.name || '同学';
  if (lvm) lvm.textContent = `Lv.${lv.level} · ${lv.xp} XP`;
}

/* ------------------------------ 事件绑定 ------------------------------ */

function bindShellEvents() {
  const tbSel = document.getElementById('sel-textbook');
  const uSel = document.getElementById('sel-unit');

  tbSel?.addEventListener('change', async e => {
    await saveSelection({ textbookId: e.target.value, unitId: '' });
    await refreshUnitPicker();
    toast('已切换教材');
    // 教材变化后重新渲染当前页（页面自己会读 state.selection）
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  });

  uSel?.addEventListener('change', async e => {
    await saveSelection({ unitId: e.target.value });
    toast('已切换单元');
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  });

  document.getElementById('student-card')?.addEventListener('click', openStudentModal);

  // 移动端侧边栏
  const sidebar = document.getElementById('sidebar');
  document.getElementById('btn-menu')?.addEventListener('click', () => sidebar.classList.toggle('open'));
  document.getElementById('main')?.addEventListener('click', () => sidebar.classList.remove('open'));

  // 内容变更后刷新侧边栏下拉
  on(EVENTS.CONTENT_CHANGED, async () => { await refreshContextPicker(); });
}

async function openStudentModal() {
  const students = await listStudents();
  const modal = openModal({
    title: '选择学习者',
    size: 'slim',
    body: `<div class="student-list">
      ${students.map(s => `
        <button class="student-row ${s.id === state.student?.id ? 'on' : ''}" data-id="${s.id}" type="button">
          <span class="student-avatar">${s.avatar || '🐻'}</span>
          <span class="grow"><strong>${escapeHtml(s.name)}</strong>
            <small class="muted">Lv.${levelFromXp(s.xp || 0).level} · ${s.xp || 0} XP</small></span>
          ${s.id === state.student?.id ? '<span class="tag green">当前</span>' : ''}
        </button>`).join('')}
    </div>
    <div class="form-grid" style="margin-top:14px">
      <div class="row" style="gap:8px">
        <input class="input" id="new-student-name" placeholder="新学习者名字" maxlength="12">
        <select class="input" id="new-student-avatar" style="max-width:90px">
          <option>🐻</option><option>🦊</option><option>🐼</option><option>🐨</option><option>🦁</option><option>🐯</option>
        </select>
        <button class="btn btn-primary" id="btn-add-student" type="button">添加</button>
      </div>
    </div>`,
  });
  if (!modal) return;

  modal.querySelectorAll('.student-row').forEach(btn => {
    btn.onclick = async () => {
      await switchStudent(btn.dataset.id);
      closeModal();
      toast('已切换学习者');
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  });

  modal.querySelector('#btn-add-student').onclick = async () => {
    const name = modal.querySelector('#new-student-name').value.trim();
    const avatar = modal.querySelector('#new-student-avatar').value;
    if (!name) return toast('请填写名字', 'warn');
    await createStudent({ name, avatar });
    closeModal();
    toast('已创建学习者');
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  };
}

/* ------------------------------ 路由表 ------------------------------ */

function registerRoutes() {
  route('#/home', renderHome);
  route('#/textbooks', renderTextbooks);
  route('#/textbooks/:textbookId', renderTextbooks);
  route('#/practice', renderPractice);
  route('#/practice/:module', renderPractice);
  route('#/flow', renderFlow);
  route('#/session', renderSession);
  route('#/games', renderGames);
  route('#/games/:gameId', renderGames);
  route('#/report', renderReport);
  route('#/review', renderReview);
  route('#/import', renderImport);
  route('#/ocr/:ocrId', renderOcrReview);
  route('#/settings', renderSettings);

  setNotFound((mount) => {
    mount.innerHTML = emptyState('🧭', '页面不存在', `路径：${currentPath()}`, '<a class="btn btn-primary" href="#/home">回到首页</a>');
  });
}

/**
 * 兼容「查询参数写在 # 前面」的链接：/index.html?storage=memory#/home
 * 有些外部链接/二维码会把参数放在问号位置，浏览器不会把它算进 hash，
 * 这里把它规整到 hash 内，保证 Hash 路由拿到完整带参路径。
 */
function normalizeEntryHash() {
  const search = location.search.replace(/^\?/, '');
  if (!search) return;
  const hash = location.hash || '#/home';
  if (hash.includes('?')) return;
  const merged = `${hash}?${search}`;
  history.replaceState(null, '', `${location.pathname}${merged}`);
}

normalizeEntryHash();

main().catch(err => {
  console.error('[app] 启动失败', err);
  const view = document.getElementById('view');
  if (view) {
    view.innerHTML = `<div class="empty"><span class="icon">⚠️</span>
      <p><strong>应用启动失败</strong></p>
      <p class="muted">${escapeHtml(String(err.message || err))}</p>
      <button class="btn btn-primary" onclick="location.reload()">重新加载</button></div>`;
  }
});
